<!-- src/views/patients/PatientList.vue -->
<template>
  <div class="patient-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Pacientes</h1>
      <router-link to="/patients/new" class="btn btn-primary">
        <i class="bi bi-plus-circle me-1"></i> Nuevo Paciente
      </router-link>
    </div>

    <!-- Search and filter bar -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="input-group">
              <span class="input-group-text" id="search-addon">
                <i class="bi bi-search"></i>
              </span>
              <input type="text" class="form-control" placeholder="Buscar paciente..." v-model="searchQuery"
                @input="searchPatients" />
            </div>
          </div>
          <div class="col-md-6 d-flex justify-content-end">
            <select class="form-select w-auto" v-model="sortBy" @change="sortPatients">
              <option value="name_asc">Nombre (A-Z)</option>
              <option value="name_desc">Nombre (Z-A)</option>
              <option value="date_asc">Fecha de nacimiento (antiguo primero)</option>
              <option value="date_desc">Fecha de nacimiento (reciente primero)</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      Error al cargar los pacientes: {{ error }}
    </div>

    <!-- Empty state -->
    <div v-else-if="displayedPatients.length === 0" class="text-center my-5">
      <div class="display-1 text-muted mb-3">
        <i class="bi bi-people"></i>
      </div>
      <h3 class="text-muted">No se encontraron pacientes</h3>
      <p v-if="searchQuery">No hay pacientes que coincidan con tu búsqueda "{{ searchQuery }}"</p>
      <p v-else>Comienza agregando un nuevo paciente</p>
      <router-link to="/patients/new" class="btn btn-primary mt-3">
        <i class="bi bi-plus-circle me-2"></i> Agregar Paciente
      </router-link>
    </div>

    <!-- Patient list -->
    <div v-else class="row g-4">
      <div v-for="patient in displayedPatients" :key="patient.id" class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex align-items-center mb-3">
              <div class="avatar-placeholder me-3">
                {{ getInitials(patient.first_name, patient.last_name) }}
              </div>
              <div>
                <h5 class="card-title mb-0">
                  {{ patient.first_name }} {{ patient.last_name }}
                </h5>
                <p class="text-muted mb-0" v-if="patient.fecha_nacimiento">
                  {{ formatAge(patient.fecha_nacimiento) }} años
                  
                </p>
              </div>
            </div>


            <div class="mb-3">
  <div v-if="patient.fecha_nacimiento" class="d-flex justify-content-between align-items-center">
    <div>
      <i class="bi bi-calendar me-2"></i>
      {{ formatDate(patient.fecha_nacimiento) }}
    </div>
    <span class="badge rounded-pill" :class="getTreatmentStatusClass(patient.treatment_status)">
      {{ getTreatmentStatusName(patient.treatment_status) }}
    </span>
  </div>
</div>

            <div class="d-flex justify-content-between mt-3">
              <button class="btn btn-sm btn-outline-primary me-2" @click="showAppointments(patient.id)">
                <i class="bi bi-calendar-check me-1"></i> Citas
              </button>

              <div>
                <router-link :to="`/patients/${patient.id}/edit`" class="btn btn-sm btn-outline-secondary me-2">
                  <i class="bi bi-pencil"></i>
                </router-link>
                <router-link :to="`/patients/${patient.id}`" class="btn btn-sm btn-primary">
                  Ver <i class="bi bi-arrow-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <nav v-if="totalPages > 1" class="mt-4">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
            <i class="bi bi-chevron-left"></i>
          </a>
        </li>

        <li v-for="page in paginationRange" :key="page" class="page-item" :class="{ active: currentPage === page }">
          <a class="page-link" href="#" @click.prevent="changePage(page)">
            {{ page }}
          </a>
        </li>

        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
            <i class="bi bi-chevron-right"></i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { patientService } from '@/services/api';

export default {
  name: 'PatientList',
  data() {
    return {
      patients: [],
      filteredPatients: [],
      loading: true,
      error: null,
      searchQuery: '',
      sortBy: 'name_asc',
      currentPage: 1,
      itemsPerPage: 9
    };
  },
  computed: {
    displayedPatients() {
      // Get the current page of patients
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPatients.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPatients.length / this.itemsPerPage);
    },
    paginationRange() {
      // Create an array with page numbers for pagination
      const range = [];
      const delta = 2; // How many pages to show before and after current page

      for (let i = Math.max(1, this.currentPage - delta);
        i <= Math.min(this.totalPages, this.currentPage + delta);
        i++) {
        range.push(i);
      }

      return range;
    }
  },
  async created() {
    try {
      await this.fetchPatients();
    } catch (error) {
      console.error('Error loading patients:', error);
      this.error = 'No se pudieron cargar los pacientes. Por favor, intenta de nuevo.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchPatients() {
      const response = await patientService.getAll();
      this.patients = response.data;
      this.filteredPatients = [...this.patients];
      this.sortPatients();
    },
    searchPatients() {
      if (!this.searchQuery.trim()) {
        this.filteredPatients = [...this.patients];
      } else {
        const query = this.searchQuery.toLowerCase().trim();
        this.filteredPatients = this.patients.filter(patient => {
          const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
          return fullName.includes(query) ||
            (patient.telefono && patient.telefono.includes(query));
        });
      }

      this.currentPage = 1; // Reset to first page on search
      this.sortPatients();
    },
    sortPatients() {
      switch (this.sortBy) {
        case 'name_asc':
          this.filteredPatients.sort((a, b) =>
            `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
          break;
        case 'name_desc':
          this.filteredPatients.sort((a, b) =>
            `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`));
          break;
        case 'date_asc':
          this.filteredPatients.sort((a, b) => {
            if (!a.fecha_nacimiento) return 1;
            if (!b.fecha_nacimiento) return -1;
            // Add T12:00:00Z to handle timezone consistently
            return new Date(a.fecha_nacimiento + 'T12:00:00Z') - new Date(b.fecha_nacimiento + 'T12:00:00Z');
          });
          break;
        case 'date_desc':
          this.filteredPatients.sort((a, b) => {
            if (!a.fecha_nacimiento) return 1;
            if (!b.fecha_nacimiento) return -1;
            // Add T12:00:00Z to handle timezone consistently
            return new Date(b.fecha_nacimiento + 'T12:00:00Z') - new Date(a.fecha_nacimiento + 'T12:00:00Z');
          });
          break;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';

      // Add T12:00:00Z to avoid timezone issues
      const date = new Date(dateString + 'T12:00:00Z');
      return date.toLocaleDateString('es-ES');
    },
    formatAge(dateString) {
      if (!dateString) return '';

      // Use the same approach to create a date that won't have timezone issues
      const birthDate = new Date(dateString + 'T12:00:00Z');
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    },
    showAppointments(patientId) {
      this.$router.push(`/appointments?patient=${patientId}`);
    },
    // Add methods for treatment status display
    getTreatmentStatusName(status) {
      const statuses = {
        'en_planificacion': 'En Planificación',
        'iniciado': 'Iniciado',
        'completado': 'Terminado',
        'en_pausa': 'En Pausa'
      };

      return statuses[status] || 'En Planificación';
    },
    getTreatmentStatusClass(status) {
      const statusClasses = {
        'en_planificacion': 'bg-info',      // Blue for planning
        'iniciado': 'bg-warning',           // Yellow for in progress
        'completado': 'bg-success',         // Green for completed
        'en_pausa': 'bg-danger'             // Red for paused
      };

      return statusClasses[status] || 'bg-info';
    }
  }
};
</script>

<style scoped>
.avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #495057;
}

/* Added styles for treatment status */
.badge {
  font-size: 0.75rem;
  font-weight: normal;
  padding: 0.35em 0.65em;
}
</style>